<template>
  <div class="dialog-mask" @click.self="onClickOutSide">
    <div class="dialog-content">
      <h3 class="dialog-title">{{ $t("copyNovelDialog.title") }}</h3>
      <p class="dialog-description">
        {{ $t("copyNovelDialog.descriptionLine1") }}<br />{{ $t("copyNovelDialog.descriptionLine2") }}
      </p>
      <hr />
      <div class="checkbox-select-all">
        <checkbox-icon :isChecked="allItemsChecked" @change="toggleSelectAll" :fill="checkBoxColor" :emitEvent="true" />
        <label @click="toggleSelectAll">{{ $t("copyNovelDialog.selectAll") }}</label>
      </div>
      <div class="checkbox-list">
        <div
          class="checkbox-item"
          v-for="item in items"
          :key="item.value"
          :class="{ disabled: item.required, indented: true }"
        >
          <checkbox-icon
            :isChecked="item.checked"
            @change="(value) => handleCheckboxChange(item, value)"
            :fill="item.required ? disableCheckBoxColor : checkBoxColor"
            :emitEvent="true"
          />
          <label
            class="is-show-label"
            :class="{ disabled: item.required }"
            @click="handleCheckboxChange(item, !item.checked)"
          >
            {{ $t(item.labelKey) }}
          </label>
        </div>
      </div>
      <p class="dialog-note">{{ $t("copyNovelDialog.note") }}</p>
      <footer>
        <button
          class="button primary"
          :class="{ disabled: isButtonDisabled }"
          :disabled="isButtonDisabled"
          @click="onPositiveClick"
        >
          {{ $t("copyNovelDialog.duplicateButton") }}
        </button>
        <a class="cancel-text" @click="onNegativeClick">{{ $t("common.cancel") }}</a>
      </footer>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import CheckboxIcon from "@/components/atoms/CheckboxIcon.vue";

type TCopyItems = {
  labelKey: string;
  value: string;
  checked: boolean;
  required: boolean;
};

export default Vue.extend({
  components: { CheckboxIcon },
  props: {
    novelId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      items: [
        { labelKey: "copyNovelDialog.items.theme", value: "theme", checked: false, required: false },
        { labelKey: "copyNovelDialog.items.plot", value: "plot", checked: false, required: false },
        { labelKey: "copyNovelDialog.items.plotMaker", value: "plotMaker", checked: false, required: false },
        { labelKey: "copyNovelDialog.items.timeline", value: "timeline", checked: false, required: false },
        { labelKey: "copyNovelDialog.items.manuscript", value: "manuscript", checked: false, required: false },
        { labelKey: "copyNovelDialog.items.character", value: "character", checked: false, required: false },
        { labelKey: "copyNovelDialog.items.worldview", value: "worldview", checked: false, required: false },
        { labelKey: "copyNovelDialog.items.correlation", value: "correlation", checked: false, required: false },
        { labelKey: "copyNovelDialog.items.material", value: "material", checked: false, required: false },
        { labelKey: "copyNovelDialog.items.memo", value: "memo", checked: false, required: false },
      ],
      checkBoxColor: "#409eff",
      disableCheckBoxColor: "#A2A2A2",
    };
  },
  computed: {
    isButtonDisabled() {
      return this.items.every((item: TCopyItems) => !item.checked);
    },
    allItemsChecked() {
      return this.items.every((item: TCopyItems) => item.checked);
    },
  },
  methods: {
    toggleSelectAll() {
      const newItems = this.items.map((item: TCopyItems) => ({
        ...item,
        checked: !this.allItemsChecked,
      }));
      this.items = newItems;
      this.$nextTick(() => {
        this.onCheckboxChange();
      });
    },
    handleCheckboxChange(item: TCopyItems, value: boolean) {
      // eslint-disable-next-line no-param-reassign
      item.checked = value;
      this.onCheckboxChange();
    },
    onCheckboxChange() {
      const characterItem = this.items.find((i: TCopyItems) => i.value === "character");
      const plotItem = this.items.find((i: TCopyItems) => i.value === "plot");
      const materialItem = this.items.find((i: TCopyItems) => i.value === "material");
      const timelineItem = this.items.find((i: TCopyItems) => i.value === "timeline");
      const correlationItem = this.items.find((i: TCopyItems) => i.value === "correlation");

      // 相関関係と時系列どちらもチェックが入っていない場合のみ登場人物のチェックは自由選択可能
      if (correlationItem && timelineItem && characterItem) {
        if (!correlationItem.checked && !timelineItem.checked) {
          characterItem.required = false;
        } else {
          characterItem.checked = true;
          characterItem.required = true;
        }
      }

      // 時系列にチェックが入っていない場合のみ、相関関係・プロット・資料のチェックは自由選択可能
      if (timelineItem && plotItem && materialItem) {
        if (!timelineItem.checked) {
          correlationItem.required = false;
          plotItem.required = false;
          materialItem.required = false;
        } else {
          correlationItem.checked = true;
          correlationItem.required = true;
          plotItem.checked = true;
          plotItem.required = true;
          materialItem.checked = true;
          materialItem.required = true;
        }
      }
    },
    onNegativeClick() {
      this.$close(false);
    },
    onPositiveClick() {
      const copySelectedData = this.allItemsChecked
        ? []
        : this.items.filter((item: TCopyItems) => item.checked).map((item: TCopyItems) => item.value);
      this.$close(copySelectedData);
    },
    onClickOutSide() {
      this.$close(false);
    },
  },
});
</script>

<style lang="scss" scoped>
.dialog-mask {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-overlay);
  z-index: 100;
}

.dialog-content {
  background-color: var(--color-background-normal);
  padding: 20px;
  border-radius: var(--radius-sm);
  max-width: 450px;
  width: 100%;
  box-shadow: var(--shadow-md);
  color: var(--color-text-normal);
}

.dialog-title {
  text-align: center;
  margin-top: 0;
}

.dialog-description {
  text-align: center;
  margin: 10px 0;
}

hr {
  border: 1px solid var(--color-border);
}

.checkbox-select-all {
  display: flex;
  align-items: center;
  padding: 10px 0;
  cursor: pointer;
  border-bottom: none;
  margin-bottom: 5px;

  label {
    margin-left: 10px;
    font-weight: var(--font-weight-bold);
    cursor: pointer;
  }
}

.checkbox-list {
  max-height: 200px;
  overflow-y: auto;
  border: 2px solid var(--color-border);
  border-radius: var(--radius-sm);
  margin-bottom: 10px;
}

.checkbox-item {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid var(--color-border);

  .checkbox-icon {
    margin-right: 10px;
    cursor: pointer;
  }
}

.checkbox-item:last-child {
  border-bottom: none;
}

.checkbox-item.disabled label {
  color: var(--color-text-low);
}

.checkbox-item.indented {
  padding-left: 15px;
}

.dialog-note {
  margin-top: 10px;
  margin-left: 10px;
  font-size: var(--font-size-xs);
  color: var(--color-text-low);
}

footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.button {
  padding: 10px 20px;
  border: none;
  border-radius: var(--radius-xs);
  cursor: pointer;
}

.button.primary {
  background-color: var(--color-primary);
  color: var(--color-text-on-fixed-bg);
  width: 50%;
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-sm);
}

.button.primary.disabled {
  background-color: var(--color-surface-muted);
  color: var(--color-text-low);
  cursor: not-allowed;
  opacity: 0.6;
}

.cancel-text {
  color: var(--color-text-low);
  cursor: pointer;
  text-decoration: underline;
  font-size: var(--font-size-sm);
}

.is-show-label {
  color: var(--color-text-normal);
  font-size: 1em;
  cursor: pointer;

  &.disabled {
    color: var(--color-text-low);
    cursor: not-allowed;
  }
}
</style>
